import React, { useEffect, useState } from 'react';
import { Page } from '@components/page';
import { LeftOutlined } from '@ant-design/icons';
import { SelectItemsBody } from './components/select-items-body';
import { SelectItemsActions } from './components/select-items-actions';
import { UserBonification } from './components/user-bonification-modal';
import {
    customerSelector,
    draftsSelector,
    fetchAvailableItems,
    fetchAvailablePaymentConditions,
    fetchConvenio,
    fetchCustos,
    fetchMoedas,
    fetchMoedasSimbolos,
    fetchOrder,
    fetchPdiscqtWithType,
    fetchSemaphores,
    orderItemsSelector,
    orderTypeSelector,
    resetBonification,
    updateMoeda,
    updateOrder,
} from '@redux/slices/order';
import { useQuery } from '@hooks/use-query';
import { useDispatch, useSelector } from 'react-redux';
import { scheduledSelector } from '@redux/slices/order/scheduled';
import { approvalMarketingSelector } from '@redux/slices/approval-marketing';
import { ApproverResponseApi } from '@models/approval/response';
import { OrderContextProvider } from './context/provider';
import { clearMinOrderState } from '@redux/slices/min-order';
import { translate } from '@components/i18n';
import { AppDispatch } from '@redux/store';
import { SelectItemsHeaderMarketingBonification } from './components/select-items-header-marketing-bonification';
import { updatePaymentCondition } from '@redux/slices/order/select-items';

// Bonificação não tem pagamento: usa sempre a condição 100, independente da
// condição cadastrada no cliente (que pode não ser válida para a BU logada).
const MARKETING_BONIFICATION_PAYMENT_CONDITION = { id: '100', description: '' };

export const MarketingBonitication: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const query = useQuery();
    const dispatch = useDispatch<AppDispatch>();
    const { drafts } = useSelector(draftsSelector);
    const { scheduled } = useSelector(scheduledSelector);
    const { customer } = useSelector(customerSelector);
    const { orderType } = useSelector(orderTypeSelector);

    const { availableApprovalMarketing } = useSelector(
        approvalMarketingSelector,
    );

    const {
        semaphores,
        custos,
        convenios,
        pdiscqt,
        order,
        selectedMoeda,
        moedaSimbolos,
        moedas,
        ...selectItemsState
    } = useSelector(orderItemsSelector);

    const isDraft = query.get('draft') === 'true';

    useEffect(() => {
        dispatch(fetchMoedas())
        dispatch(fetchMoedasSimbolos())
    }, [])

    const fetchPreData = async (): Promise<void> => {
        const arrayDispatch = []

        if (isDraft && moedas.length > 0 && moedaSimbolos.length > 0) {
            const moeda = moedas.find(item => item.codigo === order?.codigoMoeda)
            const simbolo = moedaSimbolos.find(item => item.codigoMoeda === order?.codigoMoeda)

            if (moeda) {
                dispatch(updateMoeda({
                    ...moeda,
                    simbolo: simbolo?.simbolo
                }))

                arrayDispatch.push(dispatch(fetchCustos({ moeda: moeda.codigo })).unwrap())
            }
        } else if ((!isDraft) && selectedMoeda?.codigo) {
            arrayDispatch.push(dispatch(fetchCustos({ moeda: selectedMoeda.codigo })).unwrap())
        }

        await Promise.all([
            dispatch(fetchConvenio()).unwrap(),
            dispatch(fetchPdiscqtWithType(order?.customer?.typeId)).unwrap(),
            dispatch(fetchSemaphores()).unwrap(),
            dispatch(fetchAvailablePaymentConditions()).unwrap(),
            dispatch(resetBonification()),
            dispatch(clearMinOrderState()),
        ]).catch(error => {
            console.error(error)
        })
    }

    useEffect(() => {
        if (order?.customer?.code && moedas?.length && moedaSimbolos?.length) {
            fetchPreData()
        }
    }, [order?.customer?.code, moedas?.length, moedaSimbolos?.length])


    useEffect(() => {
        const scheduledOrder = query.get('scheduled') === 'true';

        if (isDraft) { // rascunho
            const ref = query.get('ref');

            const order = scheduledOrder
                ? scheduled?.items?.find((d) => `${d.draftRef}` === ref)
                : drafts?.items?.find((d) => `${d.draftRef}` === ref);

            dispatch(fetchOrder({
                data: {
                    id: Number(ref),
                    order,
                }
            })).unwrap().then((result) => {
                const bonificationUser = Object.assign(
                    new ApproverResponseApi(),
                    availableApprovalMarketing.find(
                        (w) =>
                            `${w.aprovadorId}` ===
                            `${result?.aprovadorMarketingUsuarioId}`,
                    ),
                );

                dispatch(updateOrder({
                    userBonification: bonificationUser?.toApproverResponse(),
                }))
                // fetchOrder sobrescreve o order com a condição salva no rascunho
                dispatch(updatePaymentCondition(MARKETING_BONIFICATION_PAYMENT_CONDITION))
            }).finally(() => {
                setLoading(false)
            })
        } else { // novo pedido
            dispatch(updateOrder({ customer, orderType }));
            dispatch(updatePaymentCondition(MARKETING_BONIFICATION_PAYMENT_CONDITION))
            setLoading(false)
        }
    }, []);

    useEffect(() => {
        if (
            selectItemsState?.orderApi?.id
            && isDraft
            && order?.customer?.state
            && custos?.length > 0
            && order.paymentCondition?.id === MARKETING_BONIFICATION_PAYMENT_CONDITION.id
        ) {
            dispatch(fetchAvailableItems({
                paymentConditionId: order.paymentCondition?.id,
                draft: true,
                tempItems: selectItemsState.orderApi.toOrderRequest().tempItems,
                codigoMoeda: selectItemsState?.orderApi?.codigoMoeda
            }));
        } else if (!isDraft && custos?.length > 0 && order.paymentCondition?.id === MARKETING_BONIFICATION_PAYMENT_CONDITION.id) {
            dispatch(fetchAvailableItems({
                paymentConditionId: order.paymentCondition?.id,
                codigoMoeda: selectItemsState?.orderApi?.codigoMoeda
            }));
        }
    }, [
        order?.customer?.state,
        custos.length,
        order.paymentCondition?.id
    ])

    return (
        <OrderContextProvider>
            <Page
                title={translate('pages.newOrderBonificatoinMarketing')}
                greyBackground
                onClickGoBack
                menuIcon={<LeftOutlined />}
                loading={loading}
            >
                <UserBonification />
                <SelectItemsHeaderMarketingBonification />
                <SelectItemsBody bonification marketing />
                <SelectItemsActions bonification marketing />
            </Page>
        </OrderContextProvider>
    );
};
